import {
  pathOr,
  prop,
} from 'ramda';
import { createSelector } from 'reselect';

import { configureChips } from '../epics/auth/utils';
import { RootState } from '../types';
import { tableLimitsSelector } from './table';
import { userBalanceSelector } from './user';

/**
 * Contains all game relevant ids such as gameId, limitId, tableId
 */
export const gameSelector = (state: RootState) => state.game ?? {};

export const limitIdSelector = createSelector(
  gameSelector,
  prop('limitId')
);

export const gameIdSelector = createSelector(
  gameSelector,
  ({ gameId }) => gameId
);

export const gameNameSelector = createSelector(
  gameSelector,
  prop('name')
);

export const isGameStartedSelector = createSelector(
  gameSelector,
  (game) => game.started,
);

/**
   * Selector for the current limits of the table
   * * Currently, there are many places where we expect the current LimitsSelector to return TableLimits,
   * * but the limits are undefined until the config is fetched
   * TODO: Refactor in order for the type inference to be correct.
   */
export const currentLimitsSelector = createSelector(
  tableLimitsSelector,
  gameSelector,
  (limits, game) => (game.limitId ? limits[game.limitId] : undefined) || Object.values(limits)[0],
);

export const minBetSelector = createSelector(
  currentLimitsSelector,
  (currentLimits) => currentLimits?.Min_Bet ?? 0
);

export const minBalanceSelector = createSelector(
  currentLimitsSelector,
  (currentLimits) => currentLimits?.Min_Balance ?? 0
);

/**
 * Selector for the chips based on current limits
 * Display only the first 7 chips
 */
export const chipsSelector = createSelector(
  currentLimitsSelector,
  (limits) => (limits?.chips ? configureChips(limits.chips) : [])
);

export const validatedChipsSelector = createSelector(
  // eslint hax
  chipsSelector,
  userBalanceSelector,
  (chips, balance) => chips.map((chip) => ({
    ...chip,
    disabled: balance < chip.value,
  })),
);

export const selectedChipSelector = createSelector(
  gameSelector,
  (game) => game.selectedChip,
);

export const lastWinSelector = pathOr(0, [ 'game', 'lastWin' ]);
export const shuffleNextRoundSelector = pathOr(false, [ 'game', 'shuffleNextRound' ]);
